import type { ClientTool } from '@talk2view/sdk';

export type ToolArgs = Record<string, unknown>;

interface ToolParameter {
  type: string;
  description: string;
  enum?: string[];
}

export interface ToolValidationError {
  error: string;
  recovery: string;
}

/** Structured success payload. Tools may also return a plain object and the
 *  builder will stringify it. */
export type ToolSuccess<T extends Record<string, unknown> = Record<string, unknown>> = {
  success: true;
} & T;

/** Structured error payload. Identical shape to ToolValidationError so the
 *  model always sees the same {error, recovery} envelope. */
export type ToolError = ToolValidationError;

export type ToolResult =
  | string
  | ToolSuccess
  | ToolError
  | Record<string, unknown>;

export interface Prerequisites {
  /** When true, the tool requires a non-empty selection to run. */
  needsSelection?: boolean;
}

export interface WordToolDefinition {
  name: string;
  description: string;
  parameters: {
    type: 'object';
    properties: Record<string, ToolParameter>;
    required?: string[];
    additionalProperties?: boolean;
  };
  permission?: boolean;
  return_direct?: boolean;

  /**
   * Runs before execute. Return a validation error to short-circuit,
   * or null/undefined to proceed. Keeps input checks out of execute bodies.
   */
  validateInput?: (args: ToolArgs) => ToolValidationError | null | undefined;

  /**
   * Runs before execute (after validateInput). If returned prerequisites
   * fail, the call is short-circuited with a structured error pointing
   * the model to the tool that produces the missing state.
   */
  prerequisites?: (args: ToolArgs) => Prerequisites | null | undefined;

  /**
   * True if the call mutates user-visible document state in a way that
   * cannot be recovered without undo (delete, replace, clear).
   * Non-destructive by default.
   */
  isDestructive?: (args: ToolArgs) => boolean;

  /**
   * Short human-readable status line shown while the tool runs,
   * e.g. "Inserting 240 chars at end".
   */
  getActivityDescription?: (args: ToolArgs) => string | null;

  /**
   * The actual Office.js work. Validation and prerequisites have passed.
   * Return a JSON string OR a plain object — the builder will stringify.
   */
  execute: (args: ToolArgs) => Promise<ToolResult>;
}

export interface WordTool extends ClientTool {
  /** Optional metadata attached by buildWordTool; read by host UI, ignored by SDK. */
  meta?: {
    isDestructive?: (args: ToolArgs) => boolean;
    getActivityDescription?: (args: ToolArgs) => string | null;
    prerequisites?: (args: ToolArgs) => Prerequisites | null | undefined;
    hasValidation?: boolean;
  };
}

const MAX_RESULT_SIZE_CHARS = 100_000;

function jsonError(err: ToolValidationError): string {
  return JSON.stringify(err);
}

function safeParse(s: string): unknown {
  try {
    return JSON.parse(s);
  } catch {
    return s;
  }
}

function normalizeResult(result: ToolResult): string {
  const str = typeof result === 'string' ? result : JSON.stringify(result);
  if (str.length <= MAX_RESULT_SIZE_CHARS) return str;
  return JSON.stringify({
    error: `Result exceeds ${MAX_RESULT_SIZE_CHARS} characters (${str.length}).`,
    recovery:
      'Narrow the query, paginate with start_index/count, or request a smaller slice of the document.',
  });
}

async function checkPrerequisites(pre: Prerequisites): Promise<ToolValidationError | null> {
  if (pre.needsSelection) {
    try {
      const selectionText = await Word.run(async (context) => {
        const sel = context.document.getSelection();
        sel.load('text');
        await context.sync();
        return sel.text;
      });
      if (!selectionText || selectionText.length === 0) {
        return {
          error: 'This tool requires a non-empty selection, but nothing is selected.',
          recovery:
            'Use select_text to select text programmatically, or ask the user to highlight text, then retry.',
        };
      }
    } catch (err) {
      return {
        error: `Could not read selection: ${err instanceof Error ? err.message : String(err)}`,
        recovery: 'Ask the user to click in the document, then retry.',
      };
    }
  }
  return null;
}

export function buildWordTool(def: WordToolDefinition): WordTool {
  const {
    name,
    description,
    parameters,
    permission,
    return_direct,
    validateInput,
    prerequisites,
    isDestructive,
    getActivityDescription,
    execute,
  } = def;

  const wrappedExecute = async (args: ToolArgs): Promise<string> => {
    const t0 = performance.now();
    const activity = getActivityDescription?.(args) ?? undefined;
    // eslint-disable-next-line no-console
    console.log(
      `%c[tool ▶] ${name}`,
      'background:#1e3a8a;color:#fff;padding:2px 6px;border-radius:3px',
      { args, activity },
    );

    const done = (tag: 'validated' | 'prereq' | 'ok' | 'error', payload: string) => {
      const ms = (performance.now() - t0).toFixed(1);
      const color =
        tag === 'ok' ? '#15803d' : tag === 'error' ? '#b91c1c' : '#92400e';
      // eslint-disable-next-line no-console
      console.log(
        `%c[tool ◀] ${name} ${tag} ${ms}ms`,
        `background:${color};color:#fff;padding:2px 6px;border-radius:3px`,
        safeParse(payload),
      );
      return payload;
    };

    if (validateInput) {
      const validationError = validateInput(args);
      if (validationError) return done('validated', jsonError(validationError));
    }
    if (prerequisites) {
      const pre = prerequisites(args);
      if (pre) {
        const preError = await checkPrerequisites(pre);
        if (preError) return done('prereq', jsonError(preError));
      }
    }
    try {
      const result = await execute(args);
      const normalized = normalizeResult(result);
      return done('ok', normalized);
    } catch (err) {
      return done(
        'error',
        jsonError({
          error: `${name} failed: ${err instanceof Error ? err.message : String(err)}`,
          recovery:
            'Retry the call with verified inputs. Call get_document or get_selection to confirm the current document state.',
        }),
      );
    }
  };

  const tool: WordTool = {
    name,
    description,
    parameters,
    execute: wrappedExecute,
  };

  if (permission !== undefined) tool.permission = permission;
  if (return_direct !== undefined) tool.return_direct = return_direct;

  if (isDestructive || getActivityDescription || prerequisites || validateInput) {
    tool.meta = {};
    if (isDestructive) tool.meta.isDestructive = isDestructive;
    if (getActivityDescription) tool.meta.getActivityDescription = getActivityDescription;
    if (prerequisites) tool.meta.prerequisites = prerequisites;
    if (validateInput) tool.meta.hasValidation = true;
  }

  return tool;
}

/**
 * Introspection helper. Returns a serializable summary of every registered tool —
 * useful for debugging, docs generation, and regression tests.
 */
export function listWordTools(tools: WordTool[]): Array<{
  name: string;
  description: string;
  permission: boolean;
  required: string[];
  parameters: string[];
  destructive: boolean;
  hasValidation: boolean;
  hasPrerequisites: boolean;
}> {
  return tools.map((t) => ({
    name: t.name,
    description: t.description,
    permission: t.permission === true,
    required: (t.parameters.required as string[] | undefined) ?? [],
    parameters: Object.keys(t.parameters.properties ?? {}),
    destructive: Boolean(t.meta?.isDestructive),
    hasValidation: Boolean(t.meta?.hasValidation),
    hasPrerequisites: Boolean(t.meta?.prerequisites),
  }));
}
